import axios, { AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Reports save-intent signals (right-click, drag, long-press, Cmd/Ctrl+S)
// on preview images to /api/v3/image-intent-events. Fire-and-forget: failures
// only console.error and never affect UX. Reporting is per-studio opt-in
// (companies.image_intent_telemetry_enabled, via the #config-data bootstrap).
// The server resolves the studio from the request's domain and the gallery
// from `gallery_key`, so nothing here identifies the studio.

const API_URL = '/api/v3/image-intent-events';
const SESSION_STORAGE_KEY = 'pp_image_intent_session_uuid';
const THROTTLE_MS = 2000;

// Cmd/Ctrl+S is followed at once by the browser's own Save Page, and in Chrome
// that aborted the report's XHR ("Request aborted", seen on staging). A
// keepalive fetch is completed by the browser even when the page's own
// requests are cancelled, so that one report goes that way. XHR is the
// fallback where fetch is missing. axios's fetch adapter adds its own
// `User-Agent: axios/<version>` unless the request sets one, and Firefox sends
// it in place of the browser's; `false` leaves the browser's own in place.
const KEEPALIVE: AxiosRequestConfig = {
  adapter: ['fetch', 'xhr'],
  fetchOptions: { keepalive: true },
  headers: { 'User-Agent': false },
};

export type ImageIntentEventType =
  | 'contextmenu'
  | 'dragstart'
  | 'longpress'
  | 'save_shortcut';

export interface ImageIntentContext {
  // The gallery key the parent entered (Pics `key` / DP2 `password`). The
  // server records a gallery only when this key proves it; the ids below
  // only choose among the galleries that key matches.
  galleryKey?: string | null;
  picsGalleryId?: number | string | null;
  dp2GalleryId?: number | string | null;
  picsImageId?: number | string | null;
  // Local-only throttle discriminator for call sites that have no PicsImage
  // id (a background id, slot index, or image URL). Not sent to the server.
  imageKey?: number | string | null;
  surface?: string;
  metadata?: Record<string, unknown>;
}

interface BootstrapFlags {
  telemetryEnabled: boolean;
  saveBlockingEnabled: boolean;
}

let inMemorySessionUuid: string | null = null;
let cachedBootstrapFlags: BootstrapFlags | undefined;
const lastSentAt: Record<string, number> = {};

export const getSessionUuid = (): string => {
  try {
    let sessionUuid = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionUuid) {
      sessionUuid = uuidv4();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionUuid);
    }
    return sessionUuid;
  } catch {
    // Safari private mode can throw on sessionStorage writes
    if (!inMemorySessionUuid) {
      inMemorySessionUuid = uuidv4();
    }
    return inMemorySessionUuid;
  }
};

// The report stitches a session's anonymous attempts to the parent who signs
// in later in it, so a logout starts a new session: the next person at a shared
// tab must not inherit it. Never called on login, so the attempts a parent
// makes before signing in stay in the session they sign into.
export const resetImageIntentSession = (): void => {
  inMemorySessionUuid = null;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Safari private mode can throw on sessionStorage access
  }
};

// The per-studio flags are embedded in the SPA bootstrap payload
// (#config-data), the same source packs/index.js hydrates Redux from. Reading
// them here keeps this module dependency-free of the store, and makes each
// gate a single choke point no call site can bypass.
//   - telemetry (companies.image_intent_telemetry_enabled): off unless the
//     payload says true.
//   - save blocking (companies.image_save_blocking_enabled): off unless the
//     payload says literally true. It rolls out studio by studio, so a
//     missing or broken payload must not turn it on anywhere.
const getBootstrapFlags = (): BootstrapFlags => {
  if (cachedBootstrapFlags !== undefined) return cachedBootstrapFlags;

  try {
    const configData = document.querySelector('#config-data');
    const raw = configData?.getAttribute('data-config');
    const parsed = raw ? JSON.parse(raw) : {};
    cachedBootstrapFlags = {
      telemetryEnabled: !!parsed?.image_intent_telemetry_enabled,
      saveBlockingEnabled: parsed?.image_save_blocking_enabled === true,
    };
  } catch {
    cachedBootstrapFlags = {
      telemetryEnabled: false,
      saveBlockingEnabled: false,
    };
  }
  return cachedBootstrapFlags;
};

// The studio's switch for the save blocking this feature added (finger drags
// and the iOS long-press sheet), off unless a root admin turned it on. It
// never affects logging, and right-click blocking, which pre-dates it, stays
// on regardless.
export const isSaveBlockingEnabled = (): boolean =>
  getBootstrapFlags().saveBlockingEnabled;

// Call sites pass an object, but the .jsx ones are untyped; anything else
// would be spread into the body one character per key.
const isPlainMetadata = (
  metadata: unknown,
): metadata is Record<string, unknown> =>
  !!metadata && typeof metadata === 'object' && !Array.isArray(metadata);

const getPointerType = (): 'coarse' | 'fine' | null => {
  if (typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(pointer: coarse)').matches ? 'coarse' : 'fine';
};

export const reportImageIntent = (
  eventType: ImageIntentEventType,
  context: ImageIntentContext = {},
): void => {
  // Per-studio opt-in (companies.image_intent_telemetry_enabled): no-op
  // unless the bootstrap payload carries the flag.
  if (!getBootstrapFlags().telemetryEnabled) {
    return;
  }

  // Include every identifying facet so images without a picsImageId (e.g.
  // dashboard tiles identified only via metadata.image_id, background tiles
  // identified by imageKey) don't share one throttle bucket and suppress
  // each other's signals.
  const throttleKey = [
    eventType,
    context.surface ?? '',
    context.picsGalleryId ?? context.dp2GalleryId ?? '',
    context.picsImageId ?? context.imageKey ?? context.metadata?.image_id ?? '',
  ].join(':');
  const now = Date.now();
  if (lastSentAt[throttleKey] && now - lastSentAt[throttleKey] < THROTTLE_MS) {
    return;
  }
  lastSentAt[throttleKey] = now;

  const body = {
    event_type: eventType,
    session_uuid: getSessionUuid(),
    gallery_key: context.galleryKey ?? null,
    pics_gallery_id: context.picsGalleryId ?? null,
    dp2_gallery_id: context.dp2GalleryId ?? null,
    pics_image_id: context.picsImageId ?? null,
    metadata: {
      surface: context.surface,
      // Lets the server tell an iPad (which sends a Mac User-Agent) from a
      // Mac when it classifies the device.
      pointer: getPointerType(),
      // The page it happened on (which product, say). The path only: a
      // gallery key rides in the query string (`?k=`), so the query and
      // fragment never leave the browser, and the server keeps only route
      // words and numeric ids (ImageIntentMetadata).
      page: window.location.pathname,
      ...(isPlainMetadata(context.metadata) ? context.metadata : {}),
    },
  };
  const request =
    eventType === 'save_shortcut'
      ? axios.post(API_URL, body, KEEPALIVE)
      : axios.post(API_URL, body);

  request.catch((error) => {
    console.error('Failed to report image intent event:', error);
  });
};

// --- Cmd/Ctrl+S listener ---------------------------------------------------
// One document-level listener shared by every mounted protected surface. The
// most recently registered context wins for attribution; preventDefault only
// applies while at least one registration asked for it.

interface SaveShortcutRegistration {
  context: ImageIntentContext;
  preventDefault: boolean;
}

let saveShortcutRegistrations: SaveShortcutRegistration[] = [];
let saveShortcutListener: ((event: KeyboardEvent) => void) | null = null;

export const registerSaveShortcutContext = (
  context: ImageIntentContext,
  { preventDefault = false }: { preventDefault?: boolean } = {},
): (() => void) => {
  const registration: SaveShortcutRegistration = { context, preventDefault };
  saveShortcutRegistrations.push(registration);

  if (!saveShortcutListener) {
    saveShortcutListener = (event: KeyboardEvent) => {
      // Chrome's autofill and some extensions dispatch keydown events with
      // no `key`.
      if (
        !(event.metaKey || event.ctrlKey) ||
        typeof event.key !== 'string' ||
        event.key.toLowerCase() !== 's'
      ) {
        return;
      }
      const current =
        saveShortcutRegistrations[saveShortcutRegistrations.length - 1];
      if (!current) return;

      if (saveShortcutRegistrations.some((r) => r.preventDefault)) {
        event.preventDefault();
      }
      reportImageIntent('save_shortcut', current.context);
    };
    document.addEventListener('keydown', saveShortcutListener);
  }

  return () => {
    saveShortcutRegistrations = saveShortcutRegistrations.filter(
      (r) => r !== registration,
    );
    if (saveShortcutRegistrations.length === 0 && saveShortcutListener) {
      document.removeEventListener('keydown', saveShortcutListener);
      saveShortcutListener = null;
    }
  };
};

// Test-only helper: reset module state between examples.
export const __resetImageIntentReporterForTests = (): void => {
  Object.keys(lastSentAt).forEach((key) => delete lastSentAt[key]);
  inMemorySessionUuid = null;
  cachedBootstrapFlags = undefined;
  saveShortcutRegistrations = [];
  if (saveShortcutListener) {
    document.removeEventListener('keydown', saveShortcutListener);
    saveShortcutListener = null;
  }
};
